import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../services/api';
import type { PromptCatalogItem, SpecialistPrompt, SpecialistPromptScope, SpecialistReturnContract } from '../../types';

const RETURN_OPTIONS: SpecialistReturnContract[] = [
  'negociacao',
  'proposta',
  'handoff_humano',
  'toxico',
  'low_intent_non_actionable',
  'opt_out',
  'explicit_disinterest',
  'reativacao_intent',
];

const emptyForm = () => ({
  name: '',
  description: '',
  promptText: '',
  returnContract: 'negociacao' as SpecialistReturnContract,
  scope: 'global' as SpecialistPromptScope,
  organizationId: '',
  isActive: true,
});

export const AdminSpecialistPrompts: React.FC = () => {
  const [specialists, setSpecialists] = useState<SpecialistPrompt[]>([]);
  const [orchestratorPrompt, setOrchestratorPrompt] = useState<PromptCatalogItem | null>(null);
  const [orchestratorEditor, setOrchestratorEditor] = useState<{
    overridePrompt: string;
  }>({
    overridePrompt: '',
  });
  const [loading, setLoading] = useState(false);
  const [scopeFilter, setScopeFilter] = useState<'' | SpecialistPromptScope>('');
  const [orgFilter, setOrgFilter] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [savingOrchestrator, setSavingOrchestrator] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, catalog] = await Promise.all([
        apiService.listAdminSpecialistPrompts({
          ...(scopeFilter ? { scope: scopeFilter } : {}),
          ...(orgFilter.trim() ? { organizationId: orgFilter.trim() } : {}),
        }),
        apiService.getOrchestratorPromptCatalog().catch(() => ({ prompts: [], adminHints: { profileDomainExample: '' } })),
      ]);
      setSpecialists(data);
      const orchestrator = catalog.prompts.find((p) => p.promptId === 'orchestrator') || null;
      setOrchestratorPrompt(orchestrator);
      setOrchestratorEditor({
        overridePrompt: orchestrator?.overridePrompt || '',
      });
    } catch (e) {
      console.error('specialists load', e);
      alert('Erro ao carregar especialistas');
    } finally {
      setLoading(false);
    }
  }, [scopeFilter, orgFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const startCreate = () => {
    setEditingId('__new__');
    setForm(emptyForm());
  };

  const startEdit = (row: SpecialistPrompt) => {
    setEditingId(row.specialistId);
    setForm({
      name: row.name,
      description: row.description,
      promptText: row.promptText,
      returnContract: row.returnContract,
      scope: row.scope,
      organizationId: row.organizationId || '',
      isActive: row.isActive,
    });
  };

  const cancelForm = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const submit = async () => {
    if (!form.name.trim() || !form.promptText.trim()) {
      alert('Nome e prompt são obrigatórios');
      return;
    }
    if (form.scope === 'tenant' && !form.organizationId.trim()) {
      alert('organizationId obrigatório para escopo tenant');
      return;
    }
    setSaving(true);
    try {
      if (editingId === '__new__') {
        await apiService.createAdminSpecialistPrompt({
          name: form.name,
          description: form.description,
          promptText: form.promptText,
          returnContract: form.returnContract,
          scope: form.scope,
          organizationId: form.scope === 'tenant' ? form.organizationId.trim() : undefined,
          isActive: form.isActive,
        });
      } else if (editingId) {
        await apiService.updateAdminSpecialistPrompt(editingId, {
          name: form.name,
          description: form.description,
          promptText: form.promptText,
          returnContract: form.returnContract,
          scope: form.scope,
          organizationId: form.scope === 'tenant' ? form.organizationId.trim() : null,
          isActive: form.isActive,
        });
      }
      cancelForm();
      await load();
    } catch (e: any) {
      alert(e.response?.data?.message || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id: string) => {
    if (!window.confirm('Desativar este especialista?')) return;
    try {
      await apiService.deactivateAdminSpecialistPrompt(id);
      await load();
    } catch (e: any) {
      alert(e.response?.data?.message || 'Erro ao desativar');
    }
  };

  const handleSaveOrchestrator = async () => {
    try {
      setSavingOrchestrator(true);
      const prompts = await apiService.saveOrchestratorPromptOverride('orchestrator', {
        overridePrompt: orchestratorEditor.overridePrompt,
        examplesPositive: [],
        examplesNegative: [],
        threshold: undefined,
      });
      const updated = prompts.find((p) => p.promptId === 'orchestrator') || null;
      setOrchestratorPrompt(updated);
      if (updated) {
        setOrchestratorEditor({
          overridePrompt: updated.overridePrompt || '',
        });
      }
      alert('Edição do Orquestrador salva.');
    } catch (e: any) {
      alert(e.response?.data?.message || 'Erro ao salvar override do Orquestrador');
    } finally {
      setSavingOrchestrator(false);
    }
  };

  const handleRestoreOrchestrator = async () => {
    if (!window.confirm('Restaurar padrão do Orquestrador para esta organização?')) return;
    try {
      setSavingOrchestrator(true);
      const prompts = await apiService.restoreOrchestratorPromptDefault('orchestrator');
      const updated = prompts.find((p) => p.promptId === 'orchestrator') || null;
      setOrchestratorPrompt(updated);
      setOrchestratorEditor({
        overridePrompt: updated?.overridePrompt || '',
      });
      alert('Edição do Orquestrador restaurada.');
    } catch (e: any) {
      alert(e.response?.data?.message || 'Erro ao restaurar padrão do Orquestrador');
    } finally {
      setSavingOrchestrator(false);
    }
  };

  return (
    <section className="admin-section admin-specialists">
      <h2>Especialistas IA (Super Admin)</h2>
      <p className="admin-specialists-intro">
        Catálogo global ou por organização. O runtime usa GET <code>/specialist-prompts/active</code> (gestão da
        org).
      </p>
      <div className="admin-specialists-orchestrator-note">
        <strong>Orquestrador (especial):</strong>{' '}
        {orchestratorPrompt ? (
          <>
            {orchestratorPrompt.title} — não aparece na tabela abaixo porque esta tabela lista apenas especialistas.
          </>
        ) : (
          <>não disponível no momento (falha ao carregar catálogo de orquestrador).</>
        )}
      </div>
      {orchestratorPrompt ? (
        <div className="admin-orchestrator-editor">
          <h3>Editar Orquestrador (Super Admin)</h3>
          <label>
            Prompt default (readonly)
            <textarea rows={5} value={orchestratorPrompt.defaultReadonly || ''} readOnly />
          </label>
          <label>
            Edição do Orquestrador
            <textarea
              rows={6}
              value={orchestratorEditor.overridePrompt}
              onChange={(e) => setOrchestratorEditor((s) => ({ ...s, overridePrompt: e.target.value }))}
              placeholder="Edite o texto do Orquestrador para esta configuração."
            />
          </label>
          <div className="admin-orchestrator-actions">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={savingOrchestrator}
              onClick={() => void handleSaveOrchestrator()}
            >
              Guardar edição
            </button>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              disabled={savingOrchestrator}
              onClick={() => void handleRestoreOrchestrator()}
            >
              Restaurar edição
            </button>
          </div>
        </div>
      ) : null}

      <div className="admin-specialists-toolbar">
        <label>
          Escopo
          <select
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value as '' | SpecialistPromptScope)}
          >
            <option value="">Todos</option>
            <option value="global">global</option>
            <option value="tenant">tenant</option>
          </select>
        </label>
        <label>
          organizationId (filtro)
          <input
            type="text"
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
            placeholder="opcional"
          />
        </label>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => void load()} disabled={loading}>
          Aplicar filtros
        </button>
        <button type="button" className="btn btn-outline btn-sm" onClick={startCreate}>
          Novo especialista
        </button>
      </div>

      {editingId && (
        <div className="admin-specialists-form">
          <h3>{editingId === '__new__' ? 'Criar' : 'Editar'}</h3>
          <div className="admin-specialists-form-grid">
            <label>
              Nome
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </label>
            <label>
              returnContract
              <select
                value={form.returnContract}
                onChange={(e) =>
                  setForm((f) => ({ ...f, returnContract: e.target.value as SpecialistReturnContract }))
                }
              >
                {RETURN_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
            <label>
              scope
              <select
                value={form.scope}
                onChange={(e) => setForm((f) => ({ ...f, scope: e.target.value as SpecialistPromptScope }))}
              >
                <option value="global">global</option>
                <option value="tenant">tenant</option>
              </select>
            </label>
            <label>
              organizationId (tenant)
              <input
                value={form.organizationId}
                onChange={(e) => setForm((f) => ({ ...f, organizationId: e.target.value }))}
                disabled={form.scope !== 'tenant'}
                placeholder="UUID da org"
              />
            </label>
            <label className="admin-specialists-checkbox">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
              />
              Ativo
            </label>
          </div>
          <label className="admin-specialists-full">
            Descrição
            <input
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </label>
          <label className="admin-specialists-full">
            Prompt
            <textarea
              rows={8}
              value={form.promptText}
              onChange={(e) => setForm((f) => ({ ...f, promptText: e.target.value }))}
            />
          </label>
          <div className="admin-specialists-form-actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={() => void submit()} disabled={saving}>
              Guardar
            </button>
            <button type="button" className="btn btn-outline btn-sm" onClick={cancelForm} disabled={saving}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p>A carregar…</p>
      ) : (
        <div className="admin-specialists-table-wrap">
          <table className="admin-specialists-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Escopo</th>
                <th>Org</th>
                <th>Contrato</th>
                <th>v</th>
                <th>Activo</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {specialists.map((s) => (
                <tr key={s.specialistId} className={!s.isActive ? 'inactive-row' : ''}>
                  <td>{s.name}</td>
                  <td>{s.scope}</td>
                  <td className="mono-cell">{s.organizationId || '—'}</td>
                  <td>{s.returnContract}</td>
                  <td>{s.version}</td>
                  <td>{s.isActive ? 'sim' : 'não'}</td>
                  <td className="actions-cell">
                    <button type="button" className="linkish" onClick={() => startEdit(s)}>
                      Editar
                    </button>
                    {s.isActive ? (
                      <button type="button" className="linkish danger" onClick={() => void deactivate(s.specialistId)}>
                        Desativar
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {specialists.length === 0 ? <p className="empty-state-admin">Nenhum registro com estes filtros.</p> : null}
        </div>
      )}
    </section>
  );
};
